import { Table, TableSorting } from "@/components/elements/Table";
import { EMPTY_STR } from "@/constants/string";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { ECompanyContractStatus } from "@/types/enum";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { MRT_ColumnDef, MRT_Row } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";
import { Box } from "@mui/material";
import { TGetCompanyTypeDto } from "@/api/services/admin/company/dtos/get-company-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { useCompanyList } from "@/api/services/admin/company";
import { formatCompanyId } from "@/utils/number";
import { formatDate, formatDateTime } from "@/utils/datetime";

interface TCompanyListTableProps {
  contractStatuses: ECompanyContractStatus[];
}

export const CompanyListTable: FC<TCompanyListTableProps> = ({ contractStatuses }) => {
  const { data: matchContractStatuses } = useEnum("ECompanyContractStatus");
  const navigate = useNavigate();
  const columns = useMemo<MRT_ColumnDef<TGetCompanyTypeDto>[]>(() => {
    // Get the current contract status (first one in the array)
    const currentContractStatus = contractStatuses?.[0];

    // Determine column header and field based on contract status
    const getDateColumnConfig = () => {
      switch (currentContractStatus) {
        case ECompanyContractStatus.DURING_CONTRACT:
          return {
            header: "契約開始日",
            field: "contractStartDate",
            sortName: "company.contractStartDate",
          };
        case ECompanyContractStatus.CANCELED_CONTRACT:
          return {
            header: "解約日",
            field: "contractEndDate",
            sortName: "company.contractEndDate",
          };
        case ECompanyContractStatus.TRIAL:
          return {
            header: "トライアル開始日",
            field: "trialStartDate",
            sortName: "company.trialStartDate",
          };
        case ECompanyContractStatus.TRIAL_CANCELLED:
          return {
            header: "トライアル終了日",
            field: "trialEndDate",
            sortName: "company.trialEndDate",
          };
        default:
          return {
            header: "登録日",
            field: "createdAt",
            sortName: "company.createdAt",
          };
      }
    };

    const dateColumnConfig = getDateColumnConfig();
    const defaultDateColumnConfig = {
      header: "登録日",
      field: "createdAt",
      sortName: "company.createdAt",
    };

    return [
      {
        header: "組織ID",
        id: "id",
        accessorKey: "id",
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => {
          return <Box sx={{ pl: 2 }}>{formatCompanyId(row.original.id)}</Box>;
        },
        Header: (
          <Box sx={{ pl: 2 }}>
            <TableSorting name="company.id">組織ID</TableSorting>
          </Box>
        ),
        size: 119,
      },
      {
        header: "会社・法人名",
        id: "companyName",
        accessorKey: "companyName",
        Header: <TableSorting name="company.companyName">会社・法人名</TableSorting>,
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => truncateWithEllipsis(row.original.companyName, 25),
        size: 157,
      },
      {
        header: "課金対象数",
        id: "currentMonthBillingCount",
        accessorKey: "currentMonthBillingCount",
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => row.original?.currentMonthBillingCount ?? 0,
        size: 102,
      },
      {
        header: "前月課金数",
        id: "previousMonthBillingCount",
        accessorKey: "previousMonthBillingCount",
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => row.original?.previousMonthBillingCount ?? 0,
        size: 102,
      },
      {
        header: "契約プラン",
        id: "contractPlan",
        accessorKey: "contractPlan",
        Header: <TableSorting name="company.contractPlan">契約プラン</TableSorting>,
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) =>
          row.original?.contractPlan ? truncateWithEllipsis(row.original.contractPlan, 50) : EMPTY_STR.TEXT,
        size: 134,
      },
      {
        header: "外国人数",
        id: "currentLearnerCount",
        accessorKey: "currentLearnerCount",
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => row.original?.currentLearnerCount ?? 0,
        size: 121,
      },
      {
        header: "有効アカウント数",
        id: "activeAccountLearnerCount",
        accessorKey: "activeAccountLearnerCount",
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => row.original?.activeAccountLearnerCount ?? 0,
        size: 127,
      },
      {
        header: dateColumnConfig.header,
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) =>
          formatDate(row.original[dateColumnConfig.field as keyof TGetCompanyTypeDto] as string | null | undefined),
        Header: <TableSorting name={dateColumnConfig.sortName}>{dateColumnConfig.header}</TableSorting>,
        size: 134,
      },
      {
        header: defaultDateColumnConfig.header,
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) =>
          formatDate(row.original[defaultDateColumnConfig.field as keyof TGetCompanyTypeDto] as string | null | undefined),
        Header: <TableSorting name={defaultDateColumnConfig.sortName}>{defaultDateColumnConfig.header}</TableSorting>,
        size: 106,
      },
      {
        header: "最終ログイン日時",
        id: "lastLoginTime",
        accessorKey: "lastLoginTime",
        Cell: ({ row }: { row: MRT_Row<TGetCompanyTypeDto> }) => formatDateTime(row.original.lastLoginTime),
        size: 140,
      },
    ];
  }, [matchContractStatuses, contractStatuses]);

  const { data, isLoading } = useCompanyList(contractStatuses);

  const [heightTable] = useState<string>("calc(100vh - 430px)");

  const { table } = useTable({
    columns,
    rows: data?.data ?? [],
    mutedRow: (row) => !!row.deletedAt,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.companyDetail, { id: row.id })),
    pinningColumns: ["action"],
    renderEmptyRowsFallback: ({}) => <span></span>,
  });

  return (
    <>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <Box mt={1}>
        <NewPagination totalPage={data?.pagination.totalPage} />
      </Box>
    </>
  );
};
